import { ref, computed, onUnmounted } from 'vue';
import { CustomerStatus, POLLING_CONFIG } from '../types/insight';

export type CalculationStage = 'scrape' | 'versionUpdate' | 'featurama' | 'otis';

export interface ProgressState {
  currentStage: CalculationStage | null;
  completedStages: CalculationStage[];
  overallProgress: number;
  isLoading: boolean;
  error: string | null;
  statusMessage: string;
  isTimeout: boolean;
  showErrorPopup: boolean;
}

const stageOrder: CalculationStage[] = ['scrape', 'versionUpdate', 'featurama', 'otis'];

export function useProgressTracking() {
  const currentStage = ref<CalculationStage | null>(null);
  const completedStages = ref<CalculationStage[]>([]);
  const error = ref<string | null>(null);
  const statusMessage = ref<string>('');
  const isLoading = ref<boolean>(false);
  const isTimeout = ref<boolean>(false);
  const showErrorPopup = ref<boolean>(false);
  const startTime = ref<number | null>(null);
  const timeoutTimer = ref<NodeJS.Timeout | null>(null);

  const overallProgress = computed(() => {
    const totalStages = stageOrder.length;
    const completedCount = completedStages.value.length;
    const currentStageIndex = currentStage.value
      ? stageOrder.findIndex(stage => stage === currentStage.value)
      : -1;
    if (error.value) {
      return Math.max((completedCount / totalStages) * 100, 10);
    }

    if (currentStageIndex >= 0) {
      return ((completedCount + 0.5) / totalStages) * 100;
    }

    return (completedCount / totalStages) * 100;
  });

  const progressState = computed<ProgressState>(() => ({
    currentStage: currentStage.value,
    completedStages: completedStages.value,
    overallProgress: overallProgress.value,
    isLoading: isLoading.value,
    error: error.value,
    statusMessage: statusMessage.value,
    isTimeout: isTimeout.value,
    showErrorPopup: showErrorPopup.value,
  }));

  const updateProgress = (customerStatus: CustomerStatus) => {
    // Start timeout when processing begins for the first time
    if ((customerStatus.status === 'processing' || customerStatus.status === 'start') && !startTime.value) {
      startTimeout();
    }

    isLoading.value = customerStatus.status === 'processing' || customerStatus.status === 'start';

    if (customerStatus.status === 'fail') {
      error.value = customerStatus.message || 'Processing failed. Please try again.';
      isLoading.value = false;
      showErrorPopup.value = true;
      clearTimeoutTimer();
      return;
    }

    if (customerStatus.status === 'completed') {
      completedStages.value = [...stageOrder];
      currentStage.value = null;
      isLoading.value = false;
      statusMessage.value = 'Processing completed successfully!';
      clearTimeoutTimer();
      return;
    }

    // Clear error only if we're not in a timeout state (timeout warnings should persist)
    if (!isTimeout.value) {
      error.value = null;
    }

    const stage = customerStatus.stage || customerStatus.currentStep;
    if (stage) {
      const stageIndex = stageOrder.findIndex(stageItem => stageItem === stage);

      if (stageIndex >= 0) {
        currentStage.value = stage;
        completedStages.value = stageOrder.slice(0, stageIndex);

        statusMessage.value = getStageMessage(stage);
      }
    }

    if (customerStatus.message) {
      statusMessage.value = customerStatus.message;
    }
  };

  const getStageMessage = (stage: CalculationStage): string => {
    const messages: Record<CalculationStage, string> = {
      scrape: 'Collecting your financial data from connected accounts...',
      versionUpdate: 'Processing and validating account information...',
      featurama: 'Analysing your spending patterns and financial behavior...',
      otis: 'Generating personalised insights and recommendations...',
    };
    return messages[stage];
  };

  // Start timeout timer when processing begins
  const startTimeout = () => {
    startTime.value = Date.now();

    // Clear any existing timeout
    if (timeoutTimer.value) {
      clearTimeout(timeoutTimer.value);
    }

    // Set 5-minute timeout
    timeoutTimer.value = setTimeout(() => {
      handleTimeout();
    }, POLLING_CONFIG.MAX_PROCESSING_TIME_MS);
  };

  // Handle timeout scenario - show warning but continue loading
  const handleTimeout = () => {
    try {
      isTimeout.value = true;
      // Don't stop loading - just show a timeout warning message
      // isLoading.value = false; // Commented out to keep loading
      error.value = 'We are still processing your data...';
      // Don't show error popup for timeout, just show the warning message in the loader
      // showErrorPopup.value = true; // Commented out to avoid showing popup
    } catch (error) {
      console.error('Error in handleTimeout:', error);
      // Fallback: try to show some error state but still continue loading
      try {
        // isLoading.value = false; // Commented out to keep loading
        // showErrorPopup.value = true; // Commented out to avoid showing popup
      } catch (fallbackError) {
        console.error('Fallback error in handleTimeout:', fallbackError);
      }
    }
  };

  // Clear timeout timer
  const clearTimeoutTimer = () => {
    if (timeoutTimer.value) {
      clearTimeout(timeoutTimer.value);
      timeoutTimer.value = null;
    }
  };

  const reset = () => {
    currentStage.value = null;
    completedStages.value = [];
    error.value = null;
    statusMessage.value = '';
    isLoading.value = false;
    isTimeout.value = false;
    showErrorPopup.value = false;
    startTime.value = null;
    clearTimeoutTimer();
  };

  const setError = (errorMessage: string) => {
    error.value = errorMessage;
    isLoading.value = false;
    showErrorPopup.value = true;
    clearTimeoutTimer();
  };

  // Close error popup
  const closeErrorPopup = () => {
    showErrorPopup.value = false;
  };

  // Clean up timeout on component unmount
  onUnmounted(() => {
    clearTimeoutTimer();
  });

  const completeStage = (stage: CalculationStage) => {
    const stageIndex = stageOrder.findIndex(s => s === stage);
    if (stageIndex >= 0) {
      completedStages.value = stageOrder.slice(0, stageIndex + 1);

      const nextStageIndex = stageIndex + 1;
      if (nextStageIndex < stageOrder.length) {
        const nextStage = stageOrder[nextStageIndex] as CalculationStage;
        currentStage.value = nextStage;
        statusMessage.value = getStageMessage(nextStage);
      } else {
        currentStage.value = null;
        statusMessage.value = 'Processing completed successfully!';
        isLoading.value = false;
      }
    }
  };

  return {
    progressState,
    updateProgress,
    reset,
    setError,
    completeStage,
    closeErrorPopup,
    startTimeout,
    handleTimeout,
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    showErrorPopup: computed(() => showErrorPopup.value),
  };
}
